import { useCallback } from 'react';
import { isEqual } from 'lodash-es';
import useLocalStorageState from 'use-local-storage-state';

import { type UserInfo } from '@/types';

import { normalizeStoredUser } from '../normalizeStoredUser';

// 모듈 레벨 상수 — 라이브러리의 setState 가 stringify 를 useCallback dep 으로 쓴다.
// 렌더마다 새 객체를 넘기면 setUser 정체성이 흔들려 #185 핑퐁이 난다.
const quizUserSerializer = {
  stringify: JSON.stringify,
  // 라이브러리 기본 parse 와 같은 'undefined' 처리 + 복원 정규화.
  parse: (raw: string) =>
    normalizeStoredUser(raw === 'undefined' ? undefined : JSON.parse(raw)),
};

export const useUserLocalStorageState = () => {
  const [user, setUser] = useLocalStorageState<UserInfo | null>('quiz_user', {
    defaultValue: null,
    serializer: quizUserSerializer,
  });

  // useCallback 으로 안정적인 함수 정체성을 보장한다.
  // 이 함수들은 UserContext 의 effect deps 로 쓰이는데, 매 렌더 새로 만들면
  // effect 가 불필요하게 재실행되어 인증 상태가 반복 초기화되는 문제가 있었다.
  const setUserStable = useCallback(
    (newUser: UserInfo | null) => {
      // 같은 데이터를 새 객체로 재기록해도 참조가 바뀌어 리렌더/effect 재실행이 일어나
      // setUser 핑퐁(#185 Maximum update depth)이 난다. deep-equal 이면 이전 참조를 유지한다.
      setUser(prev => (isEqual(prev, newUser) ? prev : newUser));
    },
    [setUser],
  );

  const clearUser = useCallback(() => {
    setUser(null);
  }, [setUser]);

  return {
    user,
    setUser: setUserStable,
    clearUser,
  };
};
